import { useIsVisible } from "@website/hooks/is-visible";
import { MaterialIcon } from "@website/icons/material";
import { authStore } from "@website/store/store";
import { deepCompare } from "@website/utils/deep-compare";
import { ReserveCommentAvatar } from "@website/widget/reserve-comment/reserve-comment-avavtar";
import { ReserveCommentHeader } from "@website/widget/reserve-comment/reserve-comment-header";
import { ReserveCommentPictures } from "@website/widget/reserve-comment/reserve-comment-pictures";
import { ReserveCommentPoints } from "@website/widget/reserve-comment/reserve-comment-points";
import { ReserveCommentPropertiesType } from "@website/widget/reserve-comment/reserve-comment.types";
import { ImageType } from "@website/widget/reserve-gallery/reserve-gallery.types";
import { memo, useEffect } from "react";

const Component = (properties: ReserveCommentPropertiesType) => {
  const {
    id,
    roomId,
    createdAt,
    userName,
    roomName,
    rate,
    registeredDate,
    standNights,
    comment,
    strengths,
    weakness,
    source,
    pictures,
    helpfulSubmitted,
    onUsefulnessClick,
    submitSucceed,
    onVisibleComment
  } = properties;
  const commentPicturesProperties = {
    id,
    roomName,
    standNights,
    registeredDate,
    gallery: pictures as Array<ImageType>
  };

  const { reference, inView } = useIsVisible();
  const accessToken = authStore.accessToken.get();
  useEffect(() => {
    onVisibleComment?.({
      id,
      comment,
      pictures,
      roomId,
      createdAt,
      inView
    });
  }, [comment, createdAt, id, inView, onVisibleComment, pictures, roomId]);

  return (
    <div
      className="shadow-1 bg-dim-background flex w-full flex-col gap-4 overflow-hidden rounded-xl p-4 xl:p-6"
      dir={source === "booking.com" ? "ltr" : "rtl"}
      ref={reference}
    >
      <div className="flex flex-row">
        <div className="hidden shrink pe-2 before:hidden before:size-[48px] before:rounded-full before:border before:content-[''] xl:block before:xl:inline-block">
          <ReserveCommentAvatar
            className="mx-auto mt-[-48px] size-auto"
            rate={rate}
          />
        </div>
        <div className="flex grow flex-col gap-4">
          <ReserveCommentHeader
            properties={{
              userName,
              roomName,
              registeredDate,
              standNights,
              source,
              rate
            }}
          />
          {comment && (
            <div className="text-caption xl:text-body-2">{comment}</div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {strengths && (
          <ReserveCommentPoints
            properties={{
              message: strengths,
              type: "strengths"
            }}
          />
        )}
        {weakness && (
          <ReserveCommentPoints
            properties={{
              message: weakness,
              type: "weakness"
            }}
          />
        )}
      </div>
      {!comment && !strengths && !weakness && (
        <div className="text-caption xl:text-body-2 text-on-surface-medium-emphasis">
          این کاربر صرفا امتیاز خود را ثبت کرده است.
        </div>
      )}
      {pictures && pictures?.length > 0 && (
        <div>
          <ReserveCommentPictures {...commentPicturesProperties} />
        </div>
      )}
      {onUsefulnessClick && accessToken && (
        <div
          className={`${
            !helpfulSubmitted && !submitSucceed ? "opacity-100" : "opacity-0"
          } text-caption text-on-surface-high-emphasis cursor-pointer self-end`}
          onClick={() => (onUsefulnessClick ? onUsefulnessClick(id) : null)}
        >
          <span>این دیدگاه مفید بود؟</span>
          <MaterialIcon
            className="text-on-surface-high-emphasis mx-1"
            name="check_circle"
            variant="outlined"
          />
        </div>
      )}
    </div>
  );
};
export const ReserveComment = memo(Component, deepCompare);
